// services/auth/sessionManager.js - Complete version with enhanced cleanup
import { asyncStorage } from '../storage/asyncStorageService';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

/** Session duration before expiry (30 minutes) */
const SESSION_DURATION_MS = 30 * 60 * 1000;

/** Interval for checking session validity (60 seconds / 1 minute) */
const SESSION_CHECK_INTERVAL_MS = 60 * 1000;

/** Time before expiry to show warning (2 minutes) */
const SESSION_WARNING_TIME_MS = 2 * 60 * 1000;

/** Grace period before closing connection when no active users (5 seconds) */
const DISCONNECT_GRACE_PERIOD_MS = 5000;

// ═══════════════════════════════════════════════════════════════
// SESSION MANAGER CLASS
// ═══════════════════════════════════════════════════════════════

class SessionManager {
  constructor() {
    this.SESSION_DURATION = SESSION_DURATION_MS;
    this.CHECK_INTERVAL = SESSION_CHECK_INTERVAL_MS;
    this.WARNING_TIME = SESSION_WARNING_TIME_MS;
    
    this.STORAGE_KEYS = {
      SESSION_START: 'sessionStartTime',
      LAST_ACTIVITY: 'lastActivityTime',
      SESSION_ACTIVE: 'sessionActive'
    };
    
    this.activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    this.checkInterval = null;
    this.hasShownWarning = false;
    this.warningCallback = null; // Callback for showing warning modal
    
    this.init();
  }

  init() {
    // Check if we have an existing session
    this.checkExistingSession();
    
    // Set up activity tracking
    this.setupActivityTracking();
    
    // Start session monitoring
    this.startSessionMonitoring();
  }

  checkExistingSession() {
    const sessionActive = asyncStorage.get(this.STORAGE_KEYS.SESSION_ACTIVE);
    const lastActivity = this.getLastActivity();
    const now = Date.now();
    
    if (sessionActive === 'true' && lastActivity) {
      const timeSinceActivity = now - lastActivity;
      
      if (timeSinceActivity <= this.SESSION_DURATION) {
        // Session is still valid, update activity
        this.updateActivity();
        return true;
      } else {
        // Session expired
        this.clearSession();
        return false;
      }
    }
    
    return false;
  }

  startSession() {
    const now = Date.now();
    // CRITICAL: Use setSync for immediate write to prevent race conditions
    // ProtectedRoute checks session immediately after login, so data must be available
    asyncStorage.setSync(this.STORAGE_KEYS.SESSION_START, now.toString());
    asyncStorage.setSync(this.STORAGE_KEYS.LAST_ACTIVITY, now.toString());
    asyncStorage.setSync(this.STORAGE_KEYS.SESSION_ACTIVE, 'true');
    this.hasShownWarning = false;
  }

  updateActivity() {
    if (this.isSessionActive()) {
      const now = Date.now();
      asyncStorage.set(this.STORAGE_KEYS.LAST_ACTIVITY, now.toString());
      this.hasShownWarning = false; // Reset warning flag on activity
    }
  }

  setupActivityTracking() {
    this.activityEvents.forEach(event => {
      document.addEventListener(event, () => {
        this.updateActivity();
      }, { passive: true });
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.updateActivity();
      }
    });
  }

  startSessionMonitoring() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = setInterval(() => {
      this.checkSessionValidity();
    }, this.CHECK_INTERVAL);
  }

  checkSessionValidity() {
    if (!this.isSessionActive()) return;

    const now = Date.now();
    const lastActivity = this.getLastActivity();
    
    if (!lastActivity) return;

    const timeSinceActivity = now - lastActivity;
    const timeUntilExpiry = this.SESSION_DURATION - timeSinceActivity;

    // Show warning if close to expiry
    if (timeUntilExpiry <= this.WARNING_TIME && timeUntilExpiry > 0 && !this.hasShownWarning) {
      this.showSessionWarning(Math.ceil(timeUntilExpiry / 60000)); // Minutes remaining
      this.hasShownWarning = true;
    }

    // Expire session if time is up
    if (timeSinceActivity >= this.SESSION_DURATION) {
      this.expireSession();
    }
  }

  showSessionWarning(minutesRemaining) {
    // Dispatch event for React components to show modal
    window.dispatchEvent(new CustomEvent('sessionWarning', {
      detail: { 
        minutesRemaining,
        onExtend: () => this.extendSession(),
        onLogout: () => this.expireSession()
      }
    }));
  }

  extendSession() {
    this.updateActivity(); // This will extend the session
    this.hasShownWarning = false;
    
    // Dispatch event to hide warning modal
    window.dispatchEvent(new CustomEvent('sessionExtended'));
  }

  expireSession() {
    this.clearSession();
    
    // Dispatch event for components to handle logout
    window.dispatchEvent(new CustomEvent('sessionExpired', {
      detail: { reason: 'inactivity' }
    }));
    
    // Dispatch event to show expiry message
    window.dispatchEvent(new CustomEvent('sessionExpiredMessage'));
  }

  /**
   * Enhanced clearSession with comprehensive cleanup including cache clearing
   */
  async clearSession() {
    
    // 1. Stop interval timer first to prevent any further checks
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    
    // 2. Clear all application caches
    try {
      // Import and use cache manager
      const { default: cacheManager } = await import('./cacheManager');
      cacheManager.clearAllCaches();
    } catch (error) {
      // Continue with other cleanup even if cache clearing fails
    }

    // 2b. Clear all axios HTTP response caches (critical for preventing stale data)
    try {
      const { clearAllCache } = await import('../../../config/cachedAxiosConfig');
      clearAllCache();
    } catch (error) {
      // Continue with other cleanup even if axios cache clearing fails
    }
    
    // 3. Clear session-specific data from localStorage
    Object.values(this.STORAGE_KEYS).forEach(key => {
      asyncStorage.remove(key);
    });
    
    // 4. Clear user data from localStorage
    asyncStorage.remove('userData');
    
    // 5. Clear all session storage
    sessionStorage.clear();
    
    // 6. Clear API key storage (if any)
    asyncStorage.remove('apiKey');
    asyncStorage.remove('apiKeyBackup');
    
    // 7. Clear token-related localStorage items
    const tokenKeys = [
      'accessToken',
      'idToken', 
      'refreshToken',
      'userEmail',
      'tokenExpiration'
    ];
    
    tokenKeys.forEach(key => {
      asyncStorage.remove(key);
    });

    // 8. Clear OAuth and other auth-related localStorage items
    const authKeys = [
      'pendingAuthRequest',
      'microsoft_auth_state',
      'google_auth_state',
      'ms_auth_client_id',
      'ms_auth_redirect_uri',
      'ms_auth_email',
      'google_auth_email'
    ];
    
    authKeys.forEach(key => {
      asyncStorage.remove(key);
    });

    // 9. Clear any OTP-related items that might be in localStorage
    const otpKeys = [
      'otpSession',
      'otpReference',
      'userEmail'
    ];
    
    otpKeys.forEach(key => {
      asyncStorage.remove(key);
    });
    
    // 10. Reset internal state
    this.hasShownWarning = false;
    this.warningCallback = null;
    
  }

  isSessionActive() {
    return asyncStorage.get(this.STORAGE_KEYS.SESSION_ACTIVE) === 'true';
  }

  getLastActivity() {
    const timestamp = asyncStorage.get(this.STORAGE_KEYS.LAST_ACTIVITY);
    return timestamp ? parseInt(timestamp, 10) : null;
  }

  getSessionStart() {
    const timestamp = asyncStorage.get(this.STORAGE_KEYS.SESSION_START);
    return timestamp ? parseInt(timestamp, 10) : null;
  }

  getSessionInfo() {
    if (!this.isSessionActive()) return null;

    const now = Date.now();
    const sessionStart = this.getSessionStart();
    const lastActivity = this.getLastActivity();

    if (!sessionStart || !lastActivity) return null;

    const sessionDuration = now - sessionStart;
    const timeSinceActivity = now - lastActivity;
    const timeRemaining = this.SESSION_DURATION - timeSinceActivity;

    return {
      sessionDuration,
      timeSinceActivity,
      timeRemaining: Math.max(0, timeRemaining),
      isValid: timeRemaining > 0
    };
  }

  // Check if user is authenticated with valid session
  isAuthenticated() {
    if (!this.isSessionActive()) return false;

    // Check existence of encrypted userData in storage (sync — no decrypt needed here)
    const { asyncStorage } = require('../storage/asyncStorageService');
    const hasUserData = !!asyncStorage.get('userData');

    if (!hasUserData) return false;

    return this.checkExistingSession();
  }

  /**
   * Force logout (useful for manual cleanup)
   */
  async forceLogout() {
    await this.clearSession();
    window.dispatchEvent(new CustomEvent('sessionExpired', {
      detail: { reason: 'manual' }
    }));
  }

  /**
   * Get session status for debugging
   */
  getSessionStatus() {
    return {
      isActive: this.isSessionActive(),
      sessionStart: this.getSessionStart(),
      lastActivity: this.getLastActivity(),
      hasInterval: !!this.checkInterval,
      hasShownWarning: this.hasShownWarning,
      sessionInfo: this.getSessionInfo()
    };
  }

  cleanup() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.updateActivity);
    });
  }
}

export const sessionManager = new SessionManager();